/**
 * Her map. Charcoal and chalk, held up over the room she is standing in.
 *
 * Spec §5.3: *"that entity's pattern is **remembered** and drawn into her mental
 * map: a **charcoal-and-chalk sketch overlay**, hand-drawn in style, showing
 * patrol routes as dashed lines, timings as tick marks, doors as annotated
 * boxes. It is drawn *the way a pig would draw it* — crude, smudged, only what
 * she noticed."*
 *
 * `docs/story-review.md` S-2 is why this is not a flavour feature: beat 11's
 * *"he has drawn a map of her routes"* is a **mirror**, and a mirror needs the
 * first image; beat 12 briefs a four-step plan in a game with no words and there
 * is no other way to do that. Act 3 is unbuildable without this file.
 *
 * ── Why it is SVG in the DOM and not anything in Three ──────────────────────
 *
 * `chapterCard.ts` already argued half of this and is already shipped: a symbol
 * that simply appears is a logo, and one that is drawn in front of you is
 * somebody's hand. Every raise re-draws, for that reason.
 *
 * The other half is arithmetic. **The fattening hall runs at 233 of 264 draw
 * calls and the loading yard at 281 of 290.** An overlay built the obvious way —
 * an ortho scene, a full-screen quad, a post pass — spends that headroom in every
 * room in the game, permanently, for a thing that is on screen while a key is
 * held. This costs one DOM element and no draw calls, and
 * `mental-map.spec.ts` asserts the delta is exactly 0 rather than trusting the
 * sentence.
 *
 * ── The three rules the drawing obeys ───────────────────────────────────────
 *
 * 1. **It does not rotate.** A map that turns with the camera is a HUD; a map
 *    that stays put is a memory. +X is right, +Z is up, always.
 * 2. **The sheet is her own footsteps, not a floor plan.** The wash is the union
 *    of the 1 m cells she has walked (`Ledger.wash`). Rooms she has not been in
 *    are blank paper, and a corridor she walked once is thinner than one she
 *    paced. **The shape of the farm on her map is the shape of her movement,
 *    which is not the shape of the farm.**
 * 3. **Soft is a place, sharp is a pattern.** The wash is wide, low-opacity and
 *    smudged; the marks over it are thin, opaque and deliberate. That contrast is
 *    the whole legibility system and it survives being looked at on a phone,
 *    which is where this game is reviewed (D-125).
 *
 * ── Why chalk on slate rather than ink on paper ─────────────────────────────
 *
 * The same reason `CHAPTER_CARD` inverted, and it is a physical reason rather
 * than a taste: **the map is raised in a dark building.** A sheet of paper-white
 * dropped over the fattening hall at night is the brightest thing in the game by
 * a wide margin and it would blind the player for the two seconds after they let
 * go. So it is the card's own pair — warm near-black ground, warm off-white
 * chalk — which is also a register the building is full of: pen cards, dosing
 * boards, the tally by the door.
 *
 * ── Why the jitter is seeded ────────────────────────────────────────────────
 *
 * Crude means *wrong*, not sloppy: lines bend, corners do not close, and a box is
 * four strokes that miss each other. But it must be **the same wrong map every
 * time she raises it**. A drawing that re-jitters per raise is not a memory, it
 * is video noise — the exact mistake `render/post/stack.ts` records for the paper
 * grain, made in a different medium. Every offset here comes from `mulberry32`
 * seeded by the thing being drawn, so the map is a fact about the save and not
 * about the frame.
 */

import { mulberry32 } from '@/audio/prng';
import { CHAPTER_CARD, MENTAL_MAP } from '@/render/palette';
import type { MapPoint, MarkGlyph, Observation } from '@/story/ledger';
import { PENCIL_DIGITS } from '@/render/handDigits';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Everything the drawing needs. Supplied by the caller; this file owns no state about the world. */
export interface MentalMapSheet {
  /** Visited cell INDICES for the zone, from `Ledger.wash()`. Metres = index × cell. */
  readonly wash: readonly MapPoint[];
  /** What she watched in this zone, from `Ledger.observations()`. */
  readonly marks: readonly Observation[];
  /** Metres per wash cell, from the ledger. Not restated here. */
  readonly cellMetres: number;
  /** Whose drawing this is. Absent is hers; `his` is beat 11's wall map. */
  readonly hand?: MapHand;
  /** Ruled lines of the ground itself, world metres: his sheds and fences. A closed one repeats its first point last. */
  readonly ruled?: readonly (readonly MapPoint[])[];
  /**
   * The stretches of wall she walked near, world metres (`mapWalls.ts`, D-238).
   * Drawn in her hand with the wash; they are what ties the sheet to the room.
   */
  readonly walls?: readonly (readonly MapPoint[])[];
  /**
   * Where she walked, as lines traced through her walked cells (D-241), drawn
   * as a faint dotted trail. The wash showed this and was read as a stain; with
   * it gone, an open field showed nothing but her face.
   */
  readonly trail?: readonly (readonly MapPoint[])[];
  /** Pictures drawn on the ground in the sheet's own ink: his pigs at the ends of her route (D-240). */
  readonly pictures?: readonly { readonly glyph: MarkGlyph; readonly at: MapPoint }[];
  /** Figures down the left margin, digits only (§7: no legible word). */
  readonly dates?: readonly string[];
  /**
   * Marks drawn AHEAD of her — beat 12's plan, the one time her sheet is
   * forward-looking. Not observations: the ledger holds what she saw, and
   * these are what she means to do, declared by the beat each raise.
   */
  readonly plan?: readonly PlanMark[];
}

/**
 * One mark of a plan (`docs/beat-12-plan.md`, *the three marks*): the plan's
 * one new kind and its struck state, and nothing else. `box` is a thing to
 * put out, `gate` a gate to be held, `arrow` a way to lead something; `struck`
 * is a line through it once done, the way she marks things.
 */
export interface PlanMark {
  readonly id: string;
  readonly glyph: 'box' | 'gate' | 'arrow';
  /** One point for `box` and `gate`; the polyline, tail to head, for `arrow`. */
  readonly at: readonly MapPoint[];
  readonly struck: boolean;
}

export type MapHand = 'hers' | 'his';

/**
 * What the hand changes, and it is only this. `jitter` scales every seeded
 * offset — corners, route bends — so his is 0 and his boxes close; hers is 1
 * and hers miss. **It is not a second renderer** (plan: *"a session that starts
 * writing one has misread this plan"*).
 */
const HANDS: Readonly<Record<MapHand, { readonly jitter: number; readonly closed: boolean }>> = {
  hers: { jitter: 1, closed: false },
  his: { jitter: 0, closed: true },
};

function hex(colour: number): string {
  return `#${colour.toString(16).padStart(6, '0')}`;
}

/** A stable integer seed from a string, so a mark jitters the same way forever. */
function seedOf(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

interface Bounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * The sheet's extent, in world metres, or null when she knows nothing.
 *
 * **The empty case is the point, not a guard.** A farm she has never walked is a
 * blank sheet, and `mental-map.spec.ts` asserts that before it asserts anything
 * else — a level is also what an unobserved farm reports (D-100).
 */
function boundsOf(sheet: MentalMapSheet): Bounds | null {
  // Accumulated in four plain numbers rather than by spreading arrays into
  // `Math.min`. A sheet is capped at 4000 wash cells and each contributes two
  // points, and `Math.min(...eightThousandArgs)` is an argument-limit crash
  // waiting for the one player who walked the whole farm.
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  let seen = 0;
  const include = (x: number, z: number): void => {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
    seen += 1;
  };

  for (const cell of sheet.wash) {
    include(cell.x * sheet.cellMetres, cell.z * sheet.cellMetres);
    include((cell.x + 1) * sheet.cellMetres, (cell.z + 1) * sheet.cellMetres);
  }
  for (const observation of sheet.marks) {
    for (const point of observation.mark.at) include(point.x, point.z);
  }
  for (const line of sheet.ruled ?? []) {
    for (const point of line) include(point.x, point.z);
  }
  // Walls are only ever near a walked cell, so they add a margin rather than
  // a new region — but a blank sheet with walls on it would still be framed.
  if (seen > 0) {
    for (const line of sheet.walls ?? []) {
      for (const point of line) include(point.x, point.z);
    }
  }
  for (const mark of sheet.plan ?? []) {
    for (const point of mark.at) include(point.x, point.z);
  }
  if (seen === 0) return null;
  return { minX, maxX, minZ, maxZ };
}

/** World metres to sheet units. +X right, +Z **up** — hence the negated y. */
function sx(x: number): number {
  return x;
}
function sy(z: number): number {
  return -z;
}

function moveTo(x: number, z: number): string {
  return `M ${sx(x).toFixed(3)},${sy(z).toFixed(3)}`;
}
function lineTo(x: number, z: number): string {
  return `L ${sx(x).toFixed(3)},${sy(z).toFixed(3)}`;
}

/**
 * A door: four strokes that do not meet, which is what makes it a drawing.
 *
 * Spec §5.3 calls for *"doors as annotated boxes"*. A closed rectangle is a UI
 * element; a box whose corners overshoot and undershoot is somebody's hand, and
 * it is the same trick the chapter card's marks are drawn with.
 */
function doorPath(at: MapPoint, seed: number, hand: MapHand = 'hers', size: number = MENTAL_MAP.doorMetres): string {
  const random = mulberry32(seed);
  const { jitter, closed } = HANDS[hand];
  const half = size / 2;
  const corner = (dx: number, dz: number): MapPoint => ({
    x: at.x + dx * half + (random() - 0.5) * half * 0.35 * jitter,
    z: at.z + dz * half + (random() - 0.5) * half * 0.35 * jitter,
  });
  const a = corner(-1, -1);
  const b = corner(1, -1);
  const c = corner(1, 1);
  const d = corner(-1, 1);
  // His is one closed stroke: the first point is the last. Hers is four
  // separate strokes, each starting and ending a little past its corner, so
  // the corners cross rather than close.
  if (closed) return [moveTo(a.x, a.z), lineTo(b.x, b.z), lineTo(c.x, c.z), lineTo(d.x, d.z), lineTo(a.x, a.z)].join(' ');
  return [
    moveTo(a.x, a.z), lineTo(b.x, b.z),
    moveTo(b.x, b.z), lineTo(c.x, c.z),
    moveTo(c.x, c.z), lineTo(d.x, d.z),
    moveTo(d.x, d.z), lineTo(a.x, a.z),
  ].join(' ');
}

/**
 * A route: the polyline she watched something walk, bent by hand.
 *
 * Drawn dashed by the caller (spec: *"patrol routes as dashed lines"*), so the
 * geometry here is a plain jittered polyline. Each vertex is displaced by a
 * seeded amount that grows with the length of the leg before it: a long straight
 * she watched from one end is the leg she is least sure about, and a map that is
 * confidently straight about a corridor she saw once is lying.
 */
function routePath(points: readonly MapPoint[], seed: number, hand: MapHand = 'hers'): string {
  const random = mulberry32(seed);
  const { jitter } = HANDS[hand];
  const parts: string[] = [];
  let previous: MapPoint | null = null;
  for (const point of points) {
    const leg = previous === null ? 0 : Math.hypot(point.x - previous.x, point.z - previous.z);
    const wobble = MENTAL_MAP.routeWobble * Math.min(1, 0.4 + leg / 8) * jitter;
    const x = point.x + (random() - 0.5) * wobble;
    const z = point.z + (random() - 0.5) * wobble;
    parts.push(previous === null ? moveTo(x, z) : lineTo(x, z));
    previous = point;
  }
  return parts.join(' ');
}

/**
 * Her walls: every stroke in one path, each a little past its ends and a
 * little off true, so a corner is two lines that cross rather than meet —
 * the door box's trick at room scale. Seeded by position, so the same wall is
 * the same wrong line on every raise.
 */
function wallsPath(walls: readonly (readonly MapPoint[])[]): string {
  const parts: string[] = [];
  for (const wall of walls) {
    const from = wall[0];
    const to = wall[wall.length - 1];
    if (from === undefined || to === undefined) continue;
    const random = mulberry32(seedOf(`${from.x.toFixed(2)}:${from.z.toFixed(2)}:${to.x.toFixed(2)}:${to.z.toFixed(2)}`));
    const length = Math.hypot(to.x - from.x, to.z - from.z);
    if (length <= 0) continue;
    const ux = (to.x - from.x) / length;
    const uz = (to.z - from.z) / length;
    const over = MENTAL_MAP.wallOvershootMetres;
    const wobble = (): number => (random() - 0.5) * MENTAL_MAP.wallWobbleMetres;
    parts.push(
      moveTo(from.x - ux * over * random() + wobble(), from.z - uz * over * random() + wobble()),
      lineTo(to.x + ux * over * random() + wobble(), to.z + uz * over * random() + wobble()),
    );
  }
  return parts.join(' ');
}

/**
 * Her, as a pig's face: a round head, two ears, a snout with two nostrils.
 *
 * It was a chalk cross, and blind viewers read the cross as *"danger"*,
 * *"a target"* or *"blocked"* — and one could not find a cross at all in a
 * sheet with a door box beside it. The face was named *"me"* by both viewers
 * of the mock (D-238). No heading: rule 1, the sheet does not turn and she
 * does not know which way it faces. Drawn in SVG units, not world metres,
 * because `+Z up` would put the ears under the chin.
 */
function pigFacePath(cx: number, cy: number, r: number): string {
  const p = (x: number, y: number): string => `${(cx + x * r).toFixed(3)},${(cy + y * r).toFixed(3)}`;
  const dot = (x: number, y: number): string => `M ${p(x, y)} L ${p(x, y + 0.02)}`;
  return [
    `M ${p(-1, 0)} C ${p(-1, -0.74)} ${p(-0.5, -1)} ${p(0, -1)} C ${p(0.5, -1)} ${p(1, -0.74)} ${p(1, 0)}`,
    `C ${p(1, 0.74)} ${p(0.5, 1)} ${p(0, 1)} C ${p(-0.5, 1)} ${p(-1, 0.74)} ${p(-1, 0)} Z`,
    `M ${p(-0.7, -0.56)} L ${p(-0.96, -1.22)} L ${p(-0.3, -0.92)}`,
    `M ${p(0.7, -0.56)} L ${p(0.96, -1.22)} L ${p(0.3, -0.92)}`,
    `M ${p(-0.4, 0.26)} C ${p(-0.4, -0.04)} ${p(0.4, -0.04)} ${p(0.4, 0.26)} C ${p(0.4, 0.56)} ${p(-0.4, 0.56)} ${p(-0.4, 0.26)} Z`,
    dot(-0.15, 0.24), dot(0.15, 0.24), dot(-0.4, -0.4), dot(0.4, -0.4),
  ].join(' ');
}

/**
 * What she watched, as a picture (D-239), in SVG units around (cx, cy).
 *
 * Every watched thing used to be the same hand-drawn box, and blind viewers
 * read it as *"a square with a dot"* and *"an F1 key"*: a box names nothing.
 * These are the plainest drawings of each subject a stranger can name, and
 * the door is tall because a squarish one was the *"F1"*.
 */
export function glyphPath(glyph: MarkGlyph, cx: number, cy: number, r: number): string {
  const p = (x: number, y: number): string => `${(cx + x * r).toFixed(3)},${(cy + y * r).toFixed(3)}`;
  const dot = (x: number, y: number): string => `M ${p(x, y)} L ${p(x, y + 0.04)}`;
  switch (glyph) {
    case 'door':
      return `M ${p(-0.5, 1)} L ${p(-0.5, -1)} L ${p(0.5, -1)} L ${p(0.5, 1)} M ${p(-0.8, 1)} L ${p(0.8, 1)} ${dot(0.28, 0.08)}`;
    case 'person':
      // A standing figure. Head and shoulders was tried first and read blind as
      // *"a streetlamp"* and *"a cyclops"*: at mark size it needs its legs.
      return `M ${p(0.24, -0.78)} C ${p(0.24, -1.08)} ${p(-0.24, -1.08)} ${p(-0.24, -0.78)} C ${p(-0.24, -0.48)} ${p(0.24, -0.48)} ${p(0.24, -0.78)} Z`
        + ` M ${p(0, -0.48)} L ${p(0, 0.3)} M ${p(-0.55, -0.2)} L ${p(0, -0.3)} L ${p(0.55, -0.2)}`
        + ` M ${p(-0.4, 1.05)} L ${p(0, 0.3)} L ${p(0.4, 1.05)}`;
    case 'pig':
      return sidePigPath(cx, cy, r * 1.5);
    case 'hook':
      // Beat 5's card, redrawn at mark size: an eye, a shank, an empty curl.
      return `M ${p(-0.2, -0.8)} C ${p(-0.2, -1.05)} ${p(0.2, -1.05)} ${p(0.2, -0.8)} M ${p(0, -0.8)} L ${p(0, 0.1)}`
        + ` C ${p(0, 0.65)} ${p(0.6, 0.75)} ${p(0.65, 0.3)} C ${p(0.68, 0.05)} ${p(0.4, -0.02)} ${p(0.28, 0.18)}`;
    case 'book':
      return `M ${p(0, -0.55)} C ${p(-0.35, -0.8)} ${p(-0.7, -0.8)} ${p(-0.95, -0.6)} L ${p(-0.95, 0.8)} C ${p(-0.7, 0.6)} ${p(-0.35, 0.6)} ${p(0, 0.85)}`
        + ` C ${p(0.35, 0.6)} ${p(0.7, 0.6)} ${p(0.95, 0.8)} L ${p(0.95, -0.6)} C ${p(0.7, -0.8)} ${p(0.35, -0.8)} ${p(0, -0.55)} L ${p(0, 0.85)}`;
  }
}

/**
 * Another pig, side on — body, legs, snout, tail — and never a face. The face
 * is hers alone: two faces on one sheet were read blind as *"I can't tell
 * which one is me"* (D-239). Half as big again as a glyph, because Otto and
 * the herd are bigger than she is and his pen's rails run through him.
 */
function sidePigPath(cx: number, cy: number, r: number): string {
  const p = (x: number, y: number): string => `${(cx + x * r).toFixed(3)},${(cy + y * r).toFixed(3)}`;
  return `M ${p(-0.8, 0)} C ${p(-0.8, -0.55)} ${p(-0.3, -0.62)} ${p(0.15, -0.62)} C ${p(0.6, -0.62)} ${p(0.85, -0.4)} ${p(0.9, -0.15)}`
    + ` L ${p(1.12, -0.12)} L ${p(1.12, 0.12)} L ${p(0.88, 0.14)} C ${p(0.8, 0.45)} ${p(0.5, 0.5)} ${p(0.1, 0.5)}`
    + ` C ${p(-0.35, 0.5)} ${p(-0.8, 0.45)} ${p(-0.8, 0)} Z`
    + ` M ${p(0.55, -0.55)} L ${p(0.62, -0.85)} L ${p(0.78, -0.5)}`
    + ` M ${p(-0.55, 0.45)} L ${p(-0.55, 0.85)} M ${p(-0.3, 0.5)} L ${p(-0.3, 0.85)} M ${p(0.3, 0.5)} L ${p(0.3, 0.85)} M ${p(0.55, 0.45)} L ${p(0.55, 0.85)}`
    + ` M ${p(-0.8, -0.15)} C ${p(-1.05, -0.3)} ${p(-1.05, 0.05)} ${p(-0.9, -0.05)}`;
}

/**
 * A pig in HIS hand: a livestock schematic, all straight lines — body, snout
 * block, four legs, one ear — the way a pig is drawn in a stocking plan. His
 * sheet first used her rounded side-on pig, and blind both viewers called it
 * *"cute"* and *"cartoonish"*, softening the one sheet in the game that has to
 * be cold (D-002, D-240). No tail, no curve: he does not draw them as animals.
 */
function stockPigPath(cx: number, cy: number, r: number): string {
  const p = (x: number, y: number): string => `${(cx + x * r).toFixed(3)},${(cy + y * r).toFixed(3)}`;
  return `M ${p(-1, -0.5)} L ${p(0.75, -0.5)} L ${p(0.75, 0.35)} L ${p(-1, 0.35)} Z`
    // The snout is round, with its two nostrils: the box version with a block
    // snout was read blind as *"toy trucks or bugs, not pigs"*. One curve, the
    // one feature that says pig, and nothing else soft on it.
    + ` M ${p(0.75, -0.42)} C ${p(1.2, -0.42)} ${p(1.2, 0.22)} ${p(0.75, 0.22)}`
    + ` M ${p(0.93, -0.18)} L ${p(0.93, -0.14)} M ${p(0.93, 0.0)} L ${p(0.93, 0.04)}`
    + ` M ${p(0.45, -0.5)} L ${p(0.55, -0.78)} L ${p(0.65, -0.5)}`
    + ` M ${p(-0.8, 0.35)} L ${p(-0.8, 0.8)} M ${p(-0.5, 0.35)} L ${p(-0.5, 0.8)} M ${p(0.25, 0.35)} L ${p(0.25, 0.8)} M ${p(0.55, 0.35)} L ${p(0.55, 0.8)}`;
}

/** A figure, 1-9, in her hand, `h` tall with its top-left at (left, top) in world metres. */
function figurePath(figure: string, left: number, top: number, h: number): string {
  const parts: string[] = [];
  for (const line of PENCIL_DIGITS[figure] ?? []) {
    line.forEach(([u, v], index) => {
      const x = left + u * h;
      const z = top - v * h;
      parts.push(index === 0 ? moveTo(x, z) : lineTo(x, z));
    });
  }
  return parts.join(' ');
}

/**
 * A timing: four tick marks on a short baseline.
 *
 * Spec §5.3's third form. It is deliberately NOT a number — the game has no words
 * in it (§7) and a digit is a word. Four ticks say *this happens on a count* and
 * say nothing else, which is all she could honestly have noticed.
 */
function timingPath(at: MapPoint, seed: number): string {
  const random = mulberry32(seed);
  const span = MENTAL_MAP.doorMetres;
  const parts: string[] = [moveTo(at.x - span / 2, at.z), lineTo(at.x + span / 2, at.z)];
  for (let tick = 0; tick < 4; tick += 1) {
    const t = (tick + 0.5) / 4;
    const x = at.x - span / 2 + span * t + (random() - 0.5) * 0.1;
    parts.push(moveTo(x, at.z), lineTo(x + (random() - 0.5) * 0.12, at.z + span * 0.42));
  }
  return parts.join(' ');
}

/**
 * One mark's path in either hand. Exported for `mental-map-hand.spec.ts`
 * (plan gate 6), which draws the same mark twice and requires his to close
 * with zero jitter and hers to do neither.
 */
export function markPath(kind: Observation['mark']['kind'], at: readonly MapPoint[], seed: number, hand: MapHand): string {
  const first = at[0];
  if (first === undefined) return '';
  return kind === 'route' ? routePath(at, seed, hand) : kind === 'timing' ? timingPath(first, seed) : doorPath(first, seed, hand);
}

/** How much larger a plan glyph is drawn than a remembered door box. */
const PLAN_SCALE = 1.8;

/**
 * A plan mark's glyph, in her hand. Exported for the sim lane.
 *
 * `box` is the door box with one scratch across it — the junction box, a thing
 * with something inside. `gate` is a post and a leaf standing open on an arc,
 * which is what a gate held open looks like from above. `arrow` is a route
 * with a head: the only mark on any sheet that points somewhere she has not
 * been yet.
 */
export function planPath(mark: PlanMark, seed: number): string {
  const first = mark.at[0];
  if (first === undefined) return '';
  const random = mulberry32(seed);
  // Larger than a remembered door: a plan is drawn to be read at a glance, and
  // at door size on a 30 m yard the first frame's three marks were specks.
  const size = MENTAL_MAP.doorMetres * PLAN_SCALE;
  if (mark.glyph === 'box') {
    // The box with a lightning bolt in it. It was a box with a scratch across
    // it, and blind it was *"a square with a diagonal"*; the bolt is the one
    // sign for electricity nobody has to be taught (D-239).
    const half = size / 2;
    const bolt = [
      moveTo(first.x + half * 0.15, first.z + half * 0.75), lineTo(first.x - half * 0.35, first.z - half * 0.05),
      lineTo(first.x + half * 0.3, first.z + half * 0.05), lineTo(first.x - half * 0.15, first.z - half * 0.75),
    ].join(' ');
    return `${doorPath(first, seed, 'hers', size)} ${bolt}`;
  }
  if (mark.glyph === 'gate') {
    // A field gate face on: two posts, three bars, the brace. It was a post
    // and a leaf swung on an arc — a plan view of a gate — and blind it was
    // *"an arc"* and *"the letter n"*. From above, a gate is a line (D-239).
    const wobble = (): number => (random() - 0.5) * size * 0.08;
    const w = size * 0.75;
    const h = size * 0.5;
    const parts = [
      moveTo(first.x - w, first.z - h), lineTo(first.x - w + wobble(), first.z + h),
      moveTo(first.x + w, first.z - h), lineTo(first.x + w + wobble(), first.z + h),
    ];
    for (const bar of [-0.6, 0, 0.6]) parts.push(moveTo(first.x - w, first.z + bar * h * 0.8 + wobble()), lineTo(first.x + w, first.z + bar * h * 0.8 + wobble()));
    parts.push(moveTo(first.x - w, first.z - h * 0.48), lineTo(first.x + w, first.z + h * 0.48));
    return parts.join(' ');
  }
  const route = routePath(mark.at, seed);
  const head = mark.at[mark.at.length - 1];
  const neck = mark.at[mark.at.length - 2];
  if (head === undefined || neck === undefined) return route;
  const angle = Math.atan2(head.z - neck.z, head.x - neck.x);
  const barb = size * 0.9;
  const barbs = [Math.PI * 0.82, -Math.PI * 0.82].map((turn) => {
    const a = angle + turn + (random() - 0.5) * 0.2;
    return `${moveTo(head.x, head.z)} ${lineTo(head.x + Math.cos(a) * barb, head.z + Math.sin(a) * barb)}`;
  });
  return [route, ...barbs].join(' ');
}

/** The line through a done mark: one stroke, corner to corner past its ends. */
export function strikePath(mark: PlanMark, seed: number): string {
  if (mark.at.length === 0) return '';
  const random = mulberry32(seed ^ 0x5eed);
  let minX = Infinity; let maxX = -Infinity; let minZ = Infinity; let maxZ = -Infinity;
  for (const point of mark.at) {
    minX = Math.min(minX, point.x); maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z); maxZ = Math.max(maxZ, point.z);
  }
  // Past the glyph's own ends, so it reads as crossed out rather than as a
  // fourth side or a scratch inside the box.
  const reach = MENTAL_MAP.doorMetres * PLAN_SCALE * 0.85;
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const halfX = Math.max(reach, (maxX - minX) / 2 + reach * 0.4);
  const halfZ = Math.max(reach * 0.5, (maxZ - minZ) / 2 * 0.2);
  const tilt = (random() - 0.5) * reach * 0.3;
  return `${moveTo(cx - halfX, cz - halfZ + tilt)} ${lineTo(cx + halfX, cz + halfZ - tilt)}`;
}

/** A ruled polyline: straight, exact, no seed. Only his sheets have any. */
function ruledPath(points: readonly MapPoint[]): string {
  return points.map((point, index) => (index === 0 ? moveTo(point.x, point.z) : lineTo(point.x, point.z))).join(' ');
}

/** His figures, from the ledger's own digit table, set down a column. Sheet units are metres. */
function datesPath(dates: readonly string[], left: number, top: number): string {
  const h = MENTAL_MAP.his.dateHeightMetres;
  const parts: string[] = [];
  dates.forEach((date, row) => {
    [...date].forEach((digit, column) => {
      for (const line of PENCIL_DIGITS[digit] ?? []) {
        line.forEach(([u, v], index) => {
          // `v` runs down the page, which on this sheet is −Z.
          const x = left + (column * 0.85 + u) * h;
          const z = top - (row * 1.9 + v) * h;
          parts.push(index === 0 ? moveTo(x, z) : lineTo(x, z));
        });
      }
    });
  });
  return parts.join(' ');
}

interface DrawnStroke {
  readonly element: SVGPathElement;
  readonly start: number;
  readonly span: number;
  /**
   * The dash pattern to switch to once this stroke has finished drawing, if any.
   *
   * **A dashed line cannot draw itself on.** `stroke-dashoffset` reveals a stroke
   * only while the dash pattern is one long dash and one long gap; give it a real
   * dash pattern and the same attribute merely slides the dashes along. So a
   * patrol route is drawn solid — which is what a hand does — and becomes
   * notation when the hand lifts. Found by reading the attribute contract rather
   * than by watching it fail, which is the cheaper of the two.
   */
  readonly dashOnComplete?: string;
}

/**
 * The overlay, from `raise()` to `lower()`.
 *
 * Construct once at boot and keep it; `raise` builds the drawing, `lower` fades
 * it, and `update(frameDt)` runs on the RENDER step. Never on the fixed step, and
 * never from a CSS transition — the same reason `FadeOverlay` and `ChapterCard`
 * are driven by hand: a CSS animation runs on wall-clock time and keeps running
 * behind the pause menu, which is how a thing finishes itself while nobody is
 * looking at it.
 *
 * **It never writes to the simulation.** It is handed a sheet and a position and
 * it draws them. `mental-map.spec.ts` gate 4 asserts her trace is bit-identical
 * with the map up and down, because "presentation reads state, simulation writes
 * it" is a claim this project has broken before.
 */
export class MentalMap {
  readonly #root: HTMLDivElement;
  readonly #slate: HTMLDivElement;
  readonly #svg: SVGSVGElement;
  readonly #ink: SVGGElement;
  readonly #self: SVGPathElement;
  #strokes: readonly DrawnStroke[] = [];
  #raised = false;
  #elapsed = 0;
  #opacity = 0;
  /** Her face's radius in SVG units, set per raise from the sheet's width. */
  #selfRadius = 1;
  /** A watched thing's glyph radius in SVG units, set with `#selfRadius`. */
  #glyphRadius = 1;
  /** Her cross is hers: his sheet is of a room she is not standing in. */
  #showSelf = true;
  #disposed = false;

  constructor() {
    this.#root = document.createElement('div');
    this.#root.id = 'mental-map';
    this.#root.setAttribute('aria-hidden', 'true');
    Object.assign(this.#root.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Not opaque. The room stays visible around the slate because raising the
      // map has to COST something: this is a stealth game, the world does not
      // stop, and a player who cannot see the alley at all while reading is a
      // player who has been told to stand still.
      background: `${hex(CHAPTER_CARD.ground)}${Math.round(MENTAL_MAP.veilAlpha * 255).toString(16).padStart(2, '0')}`,
      // The desaturation is what pushes the room behind the slate rather than
      // beside it. `backdrop-filter` costs no draw calls, which is the whole
      // argument of this file.
      backdropFilter: `grayscale(${MENTAL_MAP.backdropGrey}) brightness(${MENTAL_MAP.backdropDim})`,
      opacity: '0',
      pointerEvents: 'none',
      // Below the fade (5), the chapter card (6) and the pause menu. A map raised
      // as a beat hands over must not outlive the room it is a map of.
      zIndex: '4',
    } satisfies Partial<CSSStyleDeclaration>);

    // ── The slate is an OBJECT, and it took three frames to work that out ────
    //
    // The first attempt had no slate: a near-opaque veil over the whole screen
    // with strokes on it. The room went to black and the sheet read as the game
    // having switched off. The second lifted the veil until the room came back —
    // and then the marks read as a few chalk scratches floating in mid-air,
    // because there was nothing for them to be ON.
    //
    // Both were the same mistake. *Chalk on slate* is not a filter over the
    // world, it is a thing she is holding up in front of it. So the veil only
    // pushes the room back, and this element is the surface: a bounded rectangle
    // with an edge, sitting in the middle of a room you can still see.
    const slate = document.createElement('div');
    slate.dataset['role'] = 'slate';
    Object.assign(slate.style, {
      position: 'relative',
      width: '86vmin',
      height: '86vmin',
      background: hex(CHAPTER_CARD.ground),
      // Not a hairline: a slate has a thickness, and the edge is most of what
      // says this is an object rather than a hole cut in the screen.
      boxShadow: `0 0 0 ${String(MENTAL_MAP.slateEdgePx)}px ${hex(MENTAL_MAP.slateEdge)}, 0 2vmin 6vmin rgba(0,0,0,0.55)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    } satisfies Partial<CSSStyleDeclaration>);

    this.#svg = document.createElementNS(SVG_NS, 'svg');
    this.#svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    Object.assign(this.#svg.style, {
      // Inset from the slate's edge, because a drawing that runs to the rim of
      // the thing it is drawn on reads as a texture rather than as somebody's
      // marks. `MENTAL_MAP.marginMetres` is the margin in world units; this is
      // the physical one.
      width: '92%',
      height: '92%',
      overflow: 'visible',
    } satisfies Partial<CSSStyleDeclaration>);
    slate.appendChild(this.#svg);
    this.#slate = slate;

    this.#ink = document.createElementNS(SVG_NS, 'g');
    this.#svg.appendChild(this.#ink);

    // Her own position, and there is no heading arrow (`pigFacePath`, D-238).
    //
    // It is here because beat 12 briefs a plan off this sheet and a player
    // who cannot find themselves on it cannot be briefed. An ARROW would make it
    // a navigation instrument, and this is a memory — she knows where she is
    // standing, she does not know which way the drawing is facing.
    this.#self = document.createElementNS(SVG_NS, 'path');
    this.#self.setAttribute('fill', 'none');
    this.#self.setAttribute('stroke', hex(MENTAL_MAP.self));
    this.#self.setAttribute('stroke-width', String(MENTAL_MAP.selfWidthPx));
    this.#self.setAttribute('stroke-linecap', 'round');
    this.#self.setAttribute('vector-effect', 'non-scaling-stroke');
    this.#self.setAttribute('opacity', '0');
    this.#self.dataset['role'] = 'self';
    this.#svg.appendChild(this.#self);

    this.#root.appendChild(this.#slate);
    document.body.appendChild(this.#root);
  }

  get raised(): boolean {
    return this.#raised;
  }

  /** 0..1. The test seam reads this rather than sampling a pixel. */
  get opacity(): number {
    return this.#opacity;
  }

  /**
   * Raise the sheet, drawing it from scratch.
   *
   * Called on the edge, not every frame: rebuilding the DOM sixty times a second
   * would be the same drawing, drawn again, and the draw-on would never advance
   * past its first frame.
   */
  raise(sheet: MentalMapSheet): void {
    if (this.#disposed) return;
    this.#raised = true;
    this.#elapsed = 0;
    this.#showSelf = (sheet.hand ?? 'hers') === 'hers';
    this.#build(sheet);
  }

  /** Lower it. The drawing is kept until it has finished fading, then discarded. */
  lower(): void {
    this.#raised = false;
  }

  /**
   * Render step. `self` is her world position, in metres, for the cross.
   *
   * Takes the position every frame rather than at raise time because she is not
   * frozen while the map is up — rule 4 of `docs/beat-8-plan.md`, and the reason
   * raising it is a decision instead of a free action.
   */
  update(frameDt: number, self: MapPoint): void {
    if (this.#disposed) return;

    const target = this.#raised ? 1 : 0;
    const rate = this.#raised ? MENTAL_MAP.fadeIn : MENTAL_MAP.fadeOut;
    // Linear, not exponential. An eased approach never arrives, so a sheet
    // "fading out over 0.24 s" would still be faintly on screen a second and a
    // half later and the drawing would not be discarded — which is how a
    // zero-draw-call overlay quietly becomes a permanent one.
    const step = rate <= 0 ? 1 : Math.max(0, frameDt) / rate;
    this.#opacity =
      target > this.#opacity ? Math.min(target, this.#opacity + step) : Math.max(target, this.#opacity - step);
    if (!this.#raised && this.#opacity <= 0 && this.#strokes.length > 0) this.#clear();
    this.#root.style.opacity = this.#opacity.toFixed(4);

    if (this.#opacity <= 0) return;
    this.#elapsed += Math.max(0, frameDt);

    for (const stroke of this.#strokes) {
      const progress = stroke.span <= 0 ? 1 : (this.#elapsed - stroke.start) / stroke.span;
      const remaining = progress <= 0 ? 1 : progress >= 1 ? 0 : 1 - progress;
      if (progress >= 1 && stroke.dashOnComplete !== undefined) {
        if (stroke.element.getAttribute('stroke-dasharray') !== stroke.dashOnComplete) {
          // **`pathLength` has to come off first, and it shipped without.**
          // `pathLength="1"` is what lets the draw-on walk the stroke in
          // fractions without measuring it — and it also rescales every dash
          // value against a path one unit long, so the `7 6` pattern below came
          // out as a dash seven times the length of the whole route. The line
          // rendered SOLID, three lines under a comment quoting spec §5.3's
          // *"patrol routes as dashed lines"*. Found on independent review; it
          // is invisible in every gate because a solid line and a dashed line
          // have the same element count and the same draw-call cost.
          stroke.element.removeAttribute('pathLength');
          stroke.element.setAttribute('stroke-dasharray', stroke.dashOnComplete);
          stroke.element.setAttribute('stroke-dashoffset', '0');
        }
        continue;
      }
      stroke.element.setAttribute('stroke-dashoffset', remaining.toFixed(4));
    }

    // The cross arrives last, after the sheet it is standing on has been drawn.
    const crossAt = MENTAL_MAP.washSeconds + MENTAL_MAP.markSeconds;
    const shown = this.#showSelf && this.#elapsed >= crossAt ? 1 : 0;
    this.#self.setAttribute('opacity', shown.toFixed(2));
    if (shown > 0 && Number.isFinite(self.x) && Number.isFinite(self.z)) {
      this.#self.setAttribute('d', pigFacePath(sx(self.x), sy(self.z), this.#selfRadius));
    }
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#root.remove();
  }

  #clear(): void {
    while (this.#ink.firstChild) this.#ink.removeChild(this.#ink.firstChild);
    this.#strokes = [];
    this.#self.setAttribute('opacity', '0');
  }

  #build(sheet: MentalMapSheet): void {
    this.#clear();
    const hand: MapHand = sheet.hand ?? 'hers';
    const his = hand === 'his';
    // The ground is the hand's too: chalk on slate for her, pencil on paper
    // for him. Set per raise, because the same overlay shows both in one room.
    this.#slate.style.background = hex(his ? MENTAL_MAP.his.ground : CHAPTER_CARD.ground);
    this.#slate.dataset['hand'] = hand;

    let bounds = boundsOf(sheet);
    if (bounds === null) {
      // She has never been here. Blank slate, and that is the mechanic rather
      // than a failure: the map only ever holds what she noticed.
      this.#svg.removeAttribute('viewBox');
      return;
    }

    const pad = MENTAL_MAP.marginMetres;
    // Grown to the floor around its own centre, so a sheet with three cells on
    // it is a small mark on a large page rather than a scribble filling the
    // screen. See `MENTAL_MAP.minExtentMetres`.
    const grow = (min: number, max: number): { min: number; max: number } => {
      const short = MENTAL_MAP.minExtentMetres - (max - min);
      if (short <= 0) return { min, max };
      return { min: min - short / 2, max: max + short / 2 };
    };
    const spanX = grow(bounds.minX, bounds.maxX);
    const spanZ = grow(bounds.minZ, bounds.maxZ);
    bounds = { minX: spanX.min, maxX: spanX.max, minZ: spanZ.min, maxZ: spanZ.max };
    const width = bounds.maxX - bounds.minX + pad * 2;
    const height = bounds.maxZ - bounds.minZ + pad * 2;
    // Her face is a fixed share of the sheet, not a size in metres: on a 60 m
    // field a face one metre across is a speck, and in a 16 m hall a face the
    // field would need covers the alley.
    this.#selfRadius = Math.max(width, height) * MENTAL_MAP.selfSheetFraction;
    this.#glyphRadius = Math.max(width, height) * MENTAL_MAP.glyphSheetFraction;
    // +Z is up, so the viewBox's top edge is the MAXIMUM z, negated.
    this.#svg.setAttribute(
      'viewBox',
      `${(bounds.minX - pad).toFixed(3)} ${(-bounds.maxZ - pad).toFixed(3)} ${Math.max(width, 0.001).toFixed(3)} ${Math.max(height, 0.001).toFixed(3)}`,
    );

    const strokes: DrawnStroke[] = [];
    const inked = (d: string, role: string, colour: number, widthPx: number, start: number, span: number): SVGPathElement => {
      const element = document.createElementNS(SVG_NS, 'path');
      element.setAttribute('d', d);
      element.setAttribute('fill', 'none');
      element.setAttribute('stroke', hex(colour));
      element.setAttribute('stroke-width', String(widthPx));
      element.setAttribute('stroke-linecap', 'round');
      element.setAttribute('stroke-linejoin', 'round');
      element.setAttribute('vector-effect', 'non-scaling-stroke');
      element.setAttribute('pathLength', '1');
      element.setAttribute('stroke-dasharray', '1 1');
      element.setAttribute('stroke-dashoffset', '1');
      element.dataset['role'] = role;
      this.#ink.appendChild(element);
      // A solid line drawn on with `stroke-dashoffset` has to lose its dash
      // pattern when the pen lifts, or `vector-effect` leaves it dashed at the
      // pattern's screen length — the first frame of his sheet was every ruled
      // fence drawn as a broken line.
      strokes.push({ element, start, span, dashOnComplete: 'none' });
      return element;
    };

    // His ground is ruled before anything is marked on it, the way a plan is
    // drawn: the fences and the ditch first, then the dates, then her.
    for (const line of sheet.ruled ?? []) {
      if (line.length > 1) inked(ruledPath(line), 'ruled', MENTAL_MAP.his.ink, MENTAL_MAP.his.widthPx, 0, MENTAL_MAP.washSeconds);
    }
    if (sheet.dates !== undefined && sheet.dates.length > 0) {
      // Inside the page's own border, in the margin column the builder rules:
      // the border is the outermost thing he drew, so `bounds` starts at it.
      const inset = MENTAL_MAP.his.dateHeightMetres * 0.6;
      inked(datesPath(sheet.dates, bounds.minX + inset, bounds.maxZ - inset), 'dates', MENTAL_MAP.his.ink, MENTAL_MAP.his.widthPx, 0, MENTAL_MAP.washSeconds);
    }

    for (const picture of sheet.pictures ?? []) {
      const cx = sx(picture.at.x);
      const cy = sy(picture.at.z);
      const d = his && picture.glyph === 'pig' ? stockPigPath(cx, cy, this.#glyphRadius * 1.3) : glyphPath(picture.glyph, cx, cy, this.#glyphRadius);
      const drawn = inked(d, 'picture',
        his ? MENTAL_MAP.his.ink : MENTAL_MAP.mark, his ? MENTAL_MAP.his.routeWidthPx : MENTAL_MAP.markWidthPx,
        MENTAL_MAP.washSeconds, MENTAL_MAP.markSeconds);
      drawn.dataset['glyph'] = picture.glyph;
    }

    // Her trail, first and faintest: dots, which every map uses for a way walked.
    if (!his) {
      const trail = (sheet.trail ?? [])
        .filter((line) => line.length > 1)
        .map((line) => line.map((point, index) => (index === 0 ? moveTo(point.x, point.z) : lineTo(point.x, point.z))).join(' '))
        .join(' ');
      if (trail.length > 0) {
        const element = inked(trail, 'trail', MENTAL_MAP.mark, MENTAL_MAP.trailWidthPx, 0, MENTAL_MAP.washSeconds);
        element.setAttribute('stroke-opacity', String(MENTAL_MAP.trailOpacity));
        strokes[strokes.length - 1] = { element, start: 0, span: MENTAL_MAP.washSeconds, dashOnComplete: MENTAL_MAP.trailDash };
      }
    }

    // Her walls: the room she remembers, before what she saw in it.
    const walls = his ? '' : wallsPath(sheet.walls ?? []);
    if (walls.length > 0) {
      const element = inked(walls, 'wall', MENTAL_MAP.mark, MENTAL_MAP.wallWidthPx, 0, MENTAL_MAP.washSeconds);
      element.setAttribute('stroke-opacity', String(MENTAL_MAP.wallOpacity));
    }

    // **The wash is no longer drawn** (D-239). Four blind rounds called it *"a
    // stain"*, *"dirt"*, *"blood"*, *"an accident"* and *"a bug"*; once the walls
    // showed where she had been, it said nothing they did not. The walked cells
    // still decide which walls she remembers and how the sheet is framed.

    // The marks, staggered across `markSeconds` and overlapping by half — the
    // chapter card's own rhythm, and for its reason: sequential reads as a
    // machine ticking through a list, simultaneous reads as a fade, overlapping
    // is what a hand does.
    const count = sheet.marks.length;
    const slice = count === 0 ? 0 : MENTAL_MAP.markSeconds / (count / 2 + 1);
    sheet.marks.forEach((observation, index) => {
      const first = observation.mark.at[0];
      if (first === undefined) return;
      const seed = seedOf(observation.id);
      const glyph = his ? undefined : observation.mark.glyph;
      // In her hand a thing with a glyph IS the glyph; a route keeps its dashed
      // line and gets the glyph at its start, so it says who walks it.
      const d = glyph !== undefined && observation.mark.kind !== 'route'
        ? glyphPath(glyph, sx(first.x), sy(first.z), this.#glyphRadius)
        : markPath(observation.mark.kind, observation.mark.at, seed, hand);
      if (d.length === 0) return;
      if (glyph !== undefined && observation.mark.kind === 'route') {
        // At the route's middle waypoint, not its start: beat 2's round starts
        // at her own bay, and the figure sat on her face.
        const at = observation.mark.at[Math.floor(observation.mark.at.length / 2)] ?? first;
        const who = inked(glyphPath(glyph, sx(at.x), sy(at.z), this.#glyphRadius), 'mark-glyph', MENTAL_MAP.mark, MENTAL_MAP.markWidthPx,
          MENTAL_MAP.washSeconds + (index / 2) * slice, slice);
        who.dataset['markId'] = observation.id;
      }

      const element = document.createElementNS(SVG_NS, 'path');
      element.setAttribute('d', d);
      element.setAttribute('fill', 'none');
      element.setAttribute(
        'stroke',
        hex(his ? (observation.mark.kind === 'route' ? MENTAL_MAP.his.route : MENTAL_MAP.his.ink) : MENTAL_MAP.mark),
      );
      element.setAttribute('stroke-width', String(his ? (observation.mark.kind === 'route' ? MENTAL_MAP.his.routeWidthPx : MENTAL_MAP.his.widthPx) : MENTAL_MAP.markWidthPx));
      element.setAttribute('stroke-linecap', 'round');
      element.setAttribute('stroke-linejoin', 'round');
      element.setAttribute('vector-effect', 'non-scaling-stroke');
      element.setAttribute('pathLength', '1');
      element.setAttribute('stroke-dasharray', '1 1');
      element.setAttribute('stroke-dashoffset', '1');
      element.dataset['role'] = 'mark';
      element.dataset['markId'] = observation.id;
      element.dataset['markKind'] = observation.mark.kind;
      this.#ink.appendChild(element);
      strokes.push({
        element,
        start: MENTAL_MAP.washSeconds + (index / 2) * slice,
        span: slice,
        // Spec §5.3, verbatim: *"patrol routes as dashed lines"*. The pattern is
        // in px, not in metres, because it is a property of the pen and not of
        // the farm — a route across a big yard is not drawn with longer dashes.
        // Every other mark goes solid when the pen lifts, for `inked`'s reason:
        // left on its draw-on dash, a glyph longer on screen than the pattern
        // stayed half-drawn — Otto's face was a broken outline (D-239).
        dashOnComplete: observation.mark.kind === 'route' ? (his ? MENTAL_MAP.his.routeDash : MENTAL_MAP.routeDash) : 'none',
      });
    });

    // The plan is drawn last, after what she remembers: it is what she means
    // to do, laid over what she knows. A struck mark's line follows its glyph.
    const planStart = MENTAL_MAP.washSeconds + MENTAL_MAP.markSeconds * 0.5;
    const plan = sheet.plan ?? [];
    const planSlice = plan.length === 0 ? 0 : MENTAL_MAP.markSeconds / (plan.length / 2 + 1);
    // The steps joined in order by a thin solid line — glyph to glyph, then to
    // the arrow's tail. Not dotted: dots are her own trail (D-241). Figures alone were read blind as *"distance markers"* or
    // *"threat priority"*; a line through them in order is a sequence.
    const stops = plan.map((mark) => mark.at[0]).filter((point): point is MapPoint => point !== undefined);
    if (stops.length > 1) {
      const route = stops.map((point, index) => (index === 0 ? moveTo(point.x, point.z) : lineTo(point.x, point.z))).join(' ');
      const joined = inked(route, 'plan-order', MENTAL_MAP.mark, MENTAL_MAP.markWidthPx * 0.7, planStart, MENTAL_MAP.markSeconds);
      joined.setAttribute('stroke-opacity', '0.55');
    }
    plan.forEach((mark, index) => {
      const seed = seedOf(mark.id);
      const d = planPath(mark, seed);
      if (d.length === 0) return;
      const start = planStart + (index / 2) * planSlice;
      const glyph = inked(d, 'plan', MENTAL_MAP.mark, MENTAL_MAP.markWidthPx, start, planSlice);
      glyph.dataset['planId'] = mark.id;
      glyph.dataset['planGlyph'] = mark.glyph;
      // Its place in the plan, as a figure beside it. Tally strokes were tried
      // in the mock and read as *"distance markers"*; 1 2 3 are read as order
      // by everyone, and his sheet already carries figures (§7 bars words).
      const anchor = mark.at[0];
      const figure = String(index + 1);
      if (anchor !== undefined && figure.length === 1) {
        const size = MENTAL_MAP.doorMetres * PLAN_SCALE;
        const number = inked(figurePath(figure, anchor.x + size * 1.05, anchor.z + size * 1.1, size * 0.9), 'plan-number', MENTAL_MAP.mark, MENTAL_MAP.markWidthPx, start, planSlice);
        number.dataset['planId'] = mark.id;
      }
      glyph.dataset['struck'] = String(mark.struck);
      if (mark.struck) {
        const strike = inked(strikePath(mark, seed), 'strike', MENTAL_MAP.mark, MENTAL_MAP.markWidthPx * 1.3, start + planSlice, planSlice * 0.5);
        strike.dataset['planId'] = mark.id;
      }
    });

    this.#strokes = strokes;
    // Drawn once here so the first frame after `raise` is already correct: an
    // overlay whose first frame shows the finished drawing before the draw-on
    // starts is a flash, and it is the kind that only shows up on a fast machine.
    for (const stroke of strokes) stroke.element.setAttribute('stroke-dashoffset', '1');
  }
}
